import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

/**
 * White card shown while its trigger is hovered or focused (keyboard users tab onto the trigger).
 * The card's right edge lines up with the trigger's; it opens above when there is no room below.
 * Drawn in a portal, so a scrolling list or the sub nav's overflow does not clip it.
 */
const HoverCard = ({ content, children, width = 300, triggerClassName = "", ariaLabel }) => {
    const cardId = useId();
    const triggerRef = useRef(null);
    const cardRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const card = cardRef.current;
        if (!trigger || !card) return;
        const rect = trigger.getBoundingClientRect();
        const cardHeight = card.offsetHeight;
        const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
        const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.right - width, maxLeft));
        const fitsBelow = rect.bottom + GAP_PX + cardHeight <= window.innerHeight - VIEWPORT_MARGIN_PX;
        const top = fitsBelow || rect.top - GAP_PX - cardHeight < 0
            ? rect.bottom + GAP_PX
            : rect.top - GAP_PX - cardHeight;
        setPosition({ left, top });
    }, [width]);

    useLayoutEffect(() => {
        if (!isOpen) {
            setPosition(null);
            return;
        }
        updatePosition();
    }, [isOpen, updatePosition, content]);

    useEffect(() => {
        if (!isOpen) return undefined;
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isOpen, updatePosition]);

    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);
    const closeOnEscape = (event) => {
        if (event.key === "Escape") close();
    };

    return (
        <>
            <span
                ref={triggerRef}
                tabIndex={0}
                aria-describedby={isOpen ? cardId : undefined}
                aria-label={ariaLabel}
                onMouseEnter={open}
                onMouseLeave={close}
                onFocus={open}
                onBlur={close}
                onKeyDown={closeOnEscape}
                className={`focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 ${triggerClassName}`}
            >
                {children}
            </span>
            {isOpen && typeof document !== "undefined"
                ? createPortal(
                      <div
                          ref={cardRef}
                          id={cardId}
                          role="tooltip"
                          className="pointer-events-none fixed z-[100] rounded-lg border border-gray-100 bg-white p-4 text-left sh-lg"
                          style={{
                              width,
                              left: position?.left ?? 0,
                              top: position?.top ?? 0,
                              // Measured first, then shown where it fits.
                              visibility: position ? "visible" : "hidden",
                          }}
                      >
                          {content}
                      </div>,
                      document.body
                  )
                : null}
        </>
    );
};

export default HoverCard;
